import { useState } from "react";
import { Link } from "react-router";
import type { MatrixListItemUi } from "@/domains/matrix";
import { Button } from "@/shared/components/ui/button";
import { CreateMatrixDialog } from "../../../components/create-matrix-dialog";
import { MatrixSidebar } from "../../../components/matrix-sidebar";
import { MatrixTitleMenu } from "../../../components/matrix-title-menu";
import { MatrixErrorAlert } from "../../../components/matrix-error-alert";
import { MatrixCompatibilityCard } from "./matrix-compatibility-card";

type MatrixCompatibilityViewProps = {
  collectionId: string;
  matrices: MatrixListItemUi[];
  analysisIds: string[];
  onRetry: () => void;
  isRetrying: boolean;
  retryError: string | null;
};

export function MatrixCompatibilityView({
  collectionId,
  matrices,
  analysisIds,
  onRetry,
  isRetrying,
  retryError,
}: MatrixCompatibilityViewProps) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const name =
    matrices.find((matrix) => matrix.collectionId === collectionId)?.name ??
    "Matrix";

  return (
    <>
      <title>Matrix - Researchly</title>
      <div className="-mb-4 flex w-full max-w-full flex-1 flex-col overflow-hidden contain-size lg:flex-row">
        <MatrixSidebar matrices={matrices} activeCollectionId={collectionId} />
        <div className="mb-4 flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto py-4 sm:py-6 lg:px-6">
          <div className="flex items-center justify-between gap-4">
            <MatrixTitleMenu
              name={name}
              matrices={matrices}
              activeCollectionId={collectionId}
              onCreate={() => setIsCreateOpen(true)}
            />
            <Button variant="outline" disabled={isRetrying} onClick={onRetry}>
              {isRetrying ? "Retrying…" : "Retry"}
            </Button>
          </div>
          <MatrixCompatibilityCard rowCount={analysisIds.length} />
          {retryError ? (
            <MatrixErrorAlert
              title="Could not reload the matrix"
              error={{ message: retryError }}
              fallback="Could not reload this matrix. Try again."
            />
          ) : null}
          <ul className="flex flex-col items-start gap-2">
            {analysisIds.map((analysisId, index) => (
              <li key={analysisId}>
                <Button variant="link" asChild>
                  <Link to={`/reports/${analysisId}`}>
                    Open affected report {index + 1}
                  </Link>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <CreateMatrixDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} />
    </>
  );
}
